import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject, of, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { getEnumList } from 'src/app/_helpers/enum-helper';
import { LocationPickerComponent } from 'src/app/common-components/location-picker/location-picker.component';
import { AccountSettingsTab, AccountSettingsView } from 'src/app/enums/accountSettings.enum';
import { LanguageEnum } from 'src/app/enums/language.enum';
import { NotificationPreferencesEnum } from 'src/app/enums/notificationPreferences.enum';
import { TimePreference } from 'src/app/enums/timePreference.enum';
import {
    FlowrateUnit,
    GasUnit,
    PmUnit,
    RainfallUnit,
    TemperatureUnit,
    WindspeedUnit,
} from 'src/app/enums/unitPreferences.enum';
import {
    InlineEditSaveFn,
    UnitPreferenceProperty,
    UnitPreferenceRow,
    UserPreferencesDialogData,
} from 'src/app/models/accountSettings.model';
import { Lookup } from 'src/app/models/lookup.model';
import { SendPhoneVerificationCodeRequest, SmsCodeSentEvent } from 'src/app/models/phoneVerification.model';
import { UserProfile } from 'src/app/models/userProfile.model';
import { AuthService } from 'src/app/services';
import { AccountService } from 'src/app/services/account.service';
import { TimeZoneService } from 'src/app/services/timeZone.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from 'src/app/services/translate.service';
import { TypeService } from 'src/app/services/type.service';
import { UserService } from 'src/app/services/user.service';
import { ConfirmDialogComponent, ConfirmDialogData } from 'src/app/shared/confirmDialog/confirmDialog.component';
import {
    ACCOUNT_SETTINGS_DIALOG_WIDTH,
    ACCOUNT_SETTINGS_TABS,
    ACCOUNT_SETTINGS_VIEW_SIZES,
    JOB_TITLE_MAX_LENGTH,
} from 'src/app/shared/constants/accountSettings.constants';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { formatPhoneNumberForDisplay } from 'src/app/shared/utils/phone-number.util';
import { trackById } from 'src/app/shared/utils/track-by.util';

type UnitPreferences = Pick<UserProfile, UnitPreferenceProperty>;

@Component({
    standalone: false,
    selector: 'app-user-preferences-dialog',
    templateUrl: './user-preferences-dialog.component.html',
    styleUrls: ['./user-preferences-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class UserPreferencesDialogComponent implements OnInit, OnDestroy {
    readonly tabs = ACCOUNT_SETTINGS_TABS;
    readonly views = AccountSettingsView;
    readonly tabIds = AccountSettingsTab;
    readonly jobTitleMaxLength = JOB_TITLE_MAX_LENGTH;
    readonly trackById = trackById;

    savedProfile: UserProfile;
    draft: UserProfile;
    view = AccountSettingsView.Settings;
    activeTab = AccountSettingsTab.Alerts;

    timezones: Lookup[] = [];
    languages: Lookup[] = [];
    timeFormats: Lookup[] = [];
    dateFormats: Lookup[] = [];
    units: UnitPreferenceRow[] = [];

    initials = '';
    emailEnabled = false;
    smsEnabled = false;
    hasPhoneNumber = false;
    phoneVerified = false;
    phoneNumberDisplay = '';
    locationDisplay: string | null = null;
    smsConsent: SendPhoneVerificationCodeRequest | null = null;
    mfaLocked = false;
    isSaving = false;
    twoFactorBusy = false;
    twoFactorDisabling = false;

    private hasImmediateChanges = false;
    private readonly timezoneNames: Map<string, string>;
    private readonly destroy$ = new Subject<void>();

    constructor(
        private dialogRef: MatDialogRef<UserPreferencesDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: UserPreferencesDialogData,
        private userService: UserService,
        private typeService: TypeService,
        private accountService: AccountService,
        private toastService: ToastService,
        private timeZoneService: TimeZoneService,
        private authService: AuthService,
        private dialog: MatDialog,
        private translateService: TranslateService,
    ) {
        this.savedProfile = { ...data.userPreferences };
        this.draft = { ...data.userPreferences };
        this.timezoneNames = new Map(data.timezones.map((tz) => [tz.id, tz.name]));
    }

    ngOnInit(): void {
        this.dialogRef.disableClose = true;
        this.timezones = this.data.timezones.map((tz) => ({
            id: tz.id,
            name: this.timeZoneService.createTimeZoneLabel(tz.name),
        }));
        this.languages = getEnumList(LanguageEnum).map((language) => ({
            id: LanguageEnum[language as keyof typeof LanguageEnum],
            name: language,
        }));
        this.timeFormats = this.typeService.getEnumOptions(TimePreference);
        this.dateFormats = (this.data.dateFormats ?? []).map((name, id) => ({ id, name }));
        this.units = this.buildUnitRows();
        this.refreshDerivedState();

        this.dialogRef
            .keydownEvents()
            .pipe(
                filter((event) => event.key === 'Escape'),
                takeUntil(this.destroy$),
            )
            .subscribe(() => this.dismiss());
        this.dialogRef
            .backdropClick()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.dismiss());
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    selectTab(tab: AccountSettingsTab): void {
        this.activeTab = tab;
    }

    applyChange(patch: Partial<UserProfile>): void {
        this.draft = { ...this.draft, ...patch };
        this.refreshDerivedState();
    }

    toggleEmailAlerts(): void {
        this.applyChange({
            notificationPreferences: this.withChannel(
                this.draft.notificationPreferences,
                NotificationPreferencesEnum.Email,
                !this.emailEnabled,
            ),
        });
    }

    toggleSmsAlerts(): void {
        if (!this.phoneVerified) return;
        this.applyChange({
            notificationPreferences: this.withChannel(
                this.draft.notificationPreferences,
                NotificationPreferencesEnum.SMS,
                !this.smsEnabled,
            ),
        });
    }

    openSmsOptIn(): void {
        this.setView(AccountSettingsView.SmsOptIn);
    }

    onSmsCodeSent({ phoneNumber, consent }: SmsCodeSentEvent): void {
        this.smsConsent = consent;
        const patch: Partial<UserProfile> = { phoneNumber, phoneNumberConfirmed: false };
        this.commitImmediate(patch, patch);
        this.setView(AccountSettingsView.SmsVerify);
    }

    onSmsVerified(): void {
        this.commitImmediate({ phoneNumberConfirmed: true }, { phoneNumberConfirmed: true });
        const draftPreferences = this.withChannel(
            this.draft.notificationPreferences,
            NotificationPreferencesEnum.SMS,
            true,
        );
        this.persistImmediate(
            {
                notificationPreferences: this.withChannel(
                    this.savedProfile.notificationPreferences,
                    NotificationPreferencesEnum.SMS,
                    true,
                ),
            },
            { notificationPreferences: draftPreferences },
        ).subscribe({
            next: () =>
                this.toastService.showSuccess('SMS alerts enabled for {{number}}', true, {
                    number: this.phoneNumberDisplay,
                }),
            error: (err: HttpErrorResponse) => {
                this.applyChange({ notificationPreferences: draftPreferences });
                this.showSaveError(err);
            },
        });
        this.returnToSettings(AccountSettingsTab.Alerts);
    }

    readonly savePhoneNumber: InlineEditSaveFn = (value: string) => {
        const current = this.savedProfile.phoneNumber || '';
        const confirmed$ =
            this.savedProfile.phoneNumberConfirmed && current
                ? this.confirmPhoneNumberChange(current)
                : of(true);
        return confirmed$.pipe(
            filter((confirmed) => confirmed),
            switchMap(() =>
                this.persistImmediate(
                    {
                        phoneNumber: value,
                        phoneNumberConfirmed: false,
                        notificationPreferences: this.withChannel(
                            this.savedProfile.notificationPreferences,
                            NotificationPreferencesEnum.SMS,
                            false,
                        ),
                    },
                    {
                        phoneNumber: value,
                        phoneNumberConfirmed: false,
                        notificationPreferences: this.withChannel(
                            this.draft.notificationPreferences,
                            NotificationPreferencesEnum.SMS,
                            false,
                        ),
                    },
                ),
            ),
            tap(() => this.toastService.showSuccess(value ? 'Phone updated' : 'Phone cleared', true)),
            catchError((err: HttpErrorResponse) => {
                this.showSaveError(err);
                return throwError(() => err);
            }),
        );
    };

    readonly saveJobTitle: InlineEditSaveFn = (value: string) =>
        this.userService.patch({ jobTitle: value }).pipe(
            tap(() => {
                this.commitImmediate({ jobTitle: value }, { jobTitle: value });
                this.toastService.showSuccess(value ? 'Job title updated' : 'Job title cleared', true);
            }),
            catchError((err: HttpErrorResponse) => {
                this.showSaveError(err);
                return throwError(() => err);
            }),
        );

    onTwoFactorToggle(): void {
        if (this.mfaLocked || this.twoFactorBusy) return;

        const enabling = !this.draft.twoFactorEnabled;
        this.twoFactorBusy = true;
        this.accountService
            .enableMfa(enabling)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.twoFactorBusy = false;
                    this.twoFactorDisabling = !enabling;
                    this.setView(AccountSettingsView.TwoFactor);
                },
                error: (err: HttpErrorResponse) => {
                    this.twoFactorBusy = false;
                    this.toastService.showError(
                        err,
                        this.translateService.getDynamicTranslation(
                            enabling ? 'Error enabling MFA.' : 'Error disabling MFA.',
                        ),
                    );
                },
            });
    }

    onTwoFactorVerified(): void {
        const twoFactorEnabled = !this.twoFactorDisabling;
        this.commitImmediate({ twoFactorEnabled }, { twoFactorEnabled });
        this.toastService.showSuccess(
            twoFactorEnabled ? 'Two-factor authentication enabled' : 'Two-factor authentication turned off',
            true,
        );
        this.returnToSettings(AccountSettingsTab.Security);
    }

    openChangePassword(): void {
        this.setView(AccountSettingsView.ChangePassword);
    }

    onPasswordUpdated(): void {
        this.toastService.showSuccess('Password updated', true);
        this.returnToSettings(AccountSettingsTab.Security);
    }

    returnToSettings(tab: AccountSettingsTab): void {
        this.activeTab = tab;
        this.setView(AccountSettingsView.Settings);
    }

    openLocationPicker(): void {
        const dialogRef = this.dialog.open(LocationPickerComponent, {
            width: '600px',
            data: { initialPosition: this.parseDefaultMapLocation() ?? { lat: 0, lng: 0 } },
            hasBackdrop: true,
            panelClass: 'location-picker-panel',
        });
        dialogRef.componentInstance.coordinatesSelected
            .pipe(take(1), takeUntil(dialogRef.afterClosed()))
            .subscribe((coords) => {
                if (coords && Number.isFinite(coords.lat) && Number.isFinite(coords.lng)) {
                    this.applyChange({ defaultMapLocation: `${coords.lat},${coords.lng}` });
                }
                dialogRef.close();
            });
    }

    save(): void {
        if (this.isSaving) return;

        this.isSaving = true;
        const payload = { ...this.draft };
        this.userService
            .updateCurrentUserProfile(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.isSaving = false;
                    this.authService.setCurrentUserTimeZone(this.timezoneNames.get(payload.timeZoneId) || '');
                    this.toastService.showSuccess('Settings saved', true);
                    this.dialogRef.close(payload);
                },
                error: (err: HttpErrorResponse) => {
                    this.isSaving = false;
                    this.showSaveError(err);
                },
            });
    }

    cancel(): void {
        this.dialogRef.close(this.hasImmediateChanges ? this.savedProfile : undefined);
    }

    private dismiss(): void {
        if (this.view === AccountSettingsView.Settings) {
            this.cancel();
            return;
        }
        const securityViews = [AccountSettingsView.TwoFactor, AccountSettingsView.ChangePassword];
        this.returnToSettings(
            securityViews.includes(this.view) ? AccountSettingsTab.Security : AccountSettingsTab.Alerts,
        );
    }

    private setView(view: AccountSettingsView): void {
        this.view = view;
        const width = ACCOUNT_SETTINGS_VIEW_SIZES.find((size) => size.view === view)?.width;
        this.dialogRef.updateSize(width ?? ACCOUNT_SETTINGS_DIALOG_WIDTH);
    }

    private persistImmediate(savedPatch: Partial<UserProfile>, draftPatch: Partial<UserProfile>): Observable<void> {
        const next: UserProfile = { ...this.savedProfile, ...savedPatch };
        return this.userService.updateCurrentUserProfile(next).pipe(
            tap(() => this.commitImmediate(savedPatch, draftPatch)),
            map(() => undefined),
            takeUntil(this.destroy$),
        );
    }

    private commitImmediate(savedPatch: Partial<UserProfile>, draftPatch: Partial<UserProfile>): void {
        this.savedProfile = { ...this.savedProfile, ...savedPatch };
        this.draft = { ...this.draft, ...draftPatch };
        this.hasImmediateChanges = true;
        this.refreshDerivedState();
    }

    private confirmPhoneNumberChange(previousNumber: string): Observable<boolean> {
        const data: ConfirmDialogData = {
            title: this.translateService.getDynamicTranslation('Turn off SMS alerts?'),
            message: this.translateService.getDynamicTranslation(
                'Changing your mobile number turns SMS alerts off immediately, and you will need to agree to receive alerts and enter a new code before it turns back on. Email alerts are not affected.',
            ),
            primaryButtonText: this.translateService.getDynamicTranslation('Change number'),
            secondaryButtonText: this.translateService.getDynamicTranslation('Keep {{number}}', {
                number: previousNumber,
            }),
        };
        return this.dialog
            .open(ConfirmDialogComponent, { width: '480px', data })
            .afterClosed()
            .pipe(map((confirmed: boolean) => !!confirmed));
    }

    private showSaveError(err: HttpErrorResponse): void {
        this.toastService.showError(err, this.translateService.getDynamicTranslation('Failed to update preferences.'));
    }

    private withChannel(
        preferences: number[] | null | undefined,
        channel: NotificationPreferencesEnum,
        enabled: boolean,
    ): number[] {
        const next = new Set(preferences ?? []);
        if (enabled) {
            next.add(channel);
        } else {
            next.delete(channel);
        }
        return Array.from(next);
    }

    private refreshDerivedState(): void {
        const preferences = this.draft.notificationPreferences ?? [];
        this.hasPhoneNumber = !!this.draft.phoneNumber;
        this.phoneVerified = this.hasPhoneNumber && !!this.draft.phoneNumberConfirmed;
        this.phoneNumberDisplay = formatPhoneNumberForDisplay(this.draft.phoneNumber);
        this.emailEnabled = preferences.includes(NotificationPreferencesEnum.Email);
        this.smsEnabled = this.phoneVerified && preferences.includes(NotificationPreferencesEnum.SMS);
        this.locationDisplay = this.formatLocation(this.draft.defaultMapLocation);
        this.mfaLocked = !!this.draft.roleRequiresMfa;
        this.initials = this.buildInitials();
    }

    private buildInitials(): string {
        const source = (this.draft.fullName || this.draft.userName || '').trim();
        return source
            .split(/\s+/)
            .filter((part) => part.length > 0)
            .slice(0, 2)
            .map((part) => part[0].toUpperCase())
            .join('');
    }

    private formatLocation(location?: string | null): string | null {
        const coords = this.parseLocation(location);
        if (!coords) return location || null;
        return `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`;
    }

    private parseDefaultMapLocation(): { lat: number; lng: number } | undefined {
        return this.parseLocation(this.draft.defaultMapLocation);
    }

    private parseLocation(location?: string | null): { lat: number; lng: number } | undefined {
        if (!location) return undefined;
        const [lat, lng] = location.split(',').map((v) => parseFloat(v.trim()));
        if (isNaN(lat) || isNaN(lng)) return undefined;
        return { lat, lng };
    }

    private buildUnitRows(): UnitPreferenceRow[] {
        return [
            { label: 'Gas', property: nameOf<UnitPreferences>((x) => x.gasUnit), options: this.typeService.getEnumOptions(GasUnit) },
            { label: 'Flow rate', property: nameOf<UnitPreferences>((x) => x.flowRateUnit), options: this.typeService.getEnumOptions(FlowrateUnit) },
            { label: 'Temperature', property: nameOf<UnitPreferences>((x) => x.temperatureUnitId), options: this.typeService.getEnumOptions(TemperatureUnit) },
            { label: 'Rainfall', property: nameOf<UnitPreferences>((x) => x.rainfallUnit), options: this.typeService.getEnumOptions(RainfallUnit) },
            { label: 'PM', property: nameOf<UnitPreferences>((x) => x.pmUnitId), options: this.typeService.getEnumOptions(PmUnit) },
            { label: 'Wind speed', property: nameOf<UnitPreferences>((x) => x.windSpeedUnit), options: this.typeService.getEnumOptions(WindspeedUnit) },
        ];
    }
}
